import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { fetchJobDetails } from "../../../redux/slices/fetchJobDetailsSlice"; // Adjust the path as necessary
import { clearSectionJobDetails } from "../../../redux/slices/jobDetailsSlice";
import {
    selectUserDetails,
    selectUserRoles,
} from "../../../redux/slices/userSlice";
import {
    selectRegulatedPower,
    selectRegulatedBreakers,
    selectFieldDescriptions,
} from "../../../redux/selectors";
import {
    postData,
    deleteData,
} from "../../../redux/slices/apiCallSlice"; // Adjust the path as necessary
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import { postChangeLog } from "../../../redux/slices/postChangeLogSlice"; // Import postChangeLog

const RegulatedBreakers = (zone) => {
const dispatch = useDispatch();
const userDetails = useSelector(selectUserDetails);
const userRoles = useSelector(selectUserRoles);
const currentUser = userDetails?.name;
const sectionName = "regulatedBreakers";

const jobId = useSelector((state) => state.jobId.jobId);
const apiUrls = useSelector(selectApiUrls);
const apiUrl = apiUrls[sectionName];
const tableKey = sectionName;

const storedJobDetails = useSelector(selectRegulatedPower);
const fillSourceDetails = useSelector(selectRegulatedBreakers);

const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
);

const loading = storedJobDetails.length === 0;

const { formData, setFormData } = useFormState({});

const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
}, [storedJobDetails, activetab]);

const { handleFocus, handleBlur, showNotification, saveStatus } = useHandleBlur(formData);

useEffect(() => {
    const filteredSources = fillSourceDetails.filter(
        (source) =>
            source.Zone === activetab && source.ProjectID === jobDetails.ProjectID
    );

    const initialFormData = filteredSources.reduce((acc, source) => {
        acc[source.ID] = source;
        return acc;
    }, {});

    setFormData(initialFormData);
}, [fillSourceDetails, activetab, jobDetails, setFormData]);

const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
});

const [, setError] = useState(null);

useEffect(() => {
    setRoles((prevRoles) => ({ ...prevRoles, ...mapUserRoles(userRoles) }));
}, [userRoles]);

const jobCheckoutStatus = useSelector((state) => state.checkout.job);
const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

const fieldDescriptions = useSelector(selectFieldDescriptions);

const getFieldTitle = (fieldName) => {
  return (
    fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
      ?.Description || ""
  );
};

const renderTooltip = (fieldName) => (
  <Tooltip id={`tooltip-${fieldName}`}>
    {getFieldTitle(fieldName)
      .split("\n")
      .map((line, index) => (
        <span key={index}>
          {line}
          <br />
        </span>
      ))}
  </Tooltip>
);

const handleChange = (e, sourceId) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
        ...prevData,
        [sourceId]: {
            ...prevData[sourceId],
            [name]: value,
        },
    }));
};

const handleAddFillSource = async () => {
    const newBreaker = {
        projectID: jobId,
        zone: activetab,
        saleQuoteID: jobDetails.SaleQuoteID,
        supplyParentID: jobDetails.ID,
        quantity: jobDetails.Quantity || "",
        amps: jobDetails.Amps || "",
        voltage: jobDetails.Voltage || "",
    };

    // Validate newBreaker object
    if (
        !newBreaker.projectID ||
        !newBreaker.zone ||
        !newBreaker.supplyParentID
    ) {
        setError(
            "All required fields must be filled out before adding a level control."
        );
        return;
    }

    try {
        // Post the new breaker and refetch job details
        await dispatch(postData({ url: apiUrl, data: newBreaker })).unwrap();

        // Post to change log after successful addition
        const breakerDescription = `(Quantity ${newBreaker.quantity || "0"} ) ${newBreaker.amps || "0"} Amps, ${newBreaker.voltage || "0"} Voltage`;
        await dispatch(postChangeLog({
            projectID: jobId,
            currentUser: currentUser,
            userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
            section: "Regulated Breakers",
            field: breakerDescription,
            prevValue: "",
            newValue: "ADDED",
            dateTime: new Date().toISOString()
        })).unwrap();

        const updatedDetails = await dispatch(
            fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })
        ).unwrap();

        setFormData((prevData) => {
            const updatedFormData = { ...prevData };
            if (updatedDetails[sectionName]) {
                updatedDetails[sectionName].forEach((source) => {
                    if (
                        source.Zone === activetab &&
                        source.ProjectID === jobDetails.ProjectID
                    ) {
                        updatedFormData[source.ID] = source;
                    }
                });
            }
            return updatedFormData;
        });
    } catch (error) {
        console.error("Failed to add fill source:", error);
        setError(
            error.message || "An error occurred while adding the fill source."
        );
    }
};

const handleDeleteFillSource = async (sourceId) => {
    if (!sourceId) {
        setError("Invalid fill source ID.");
        return;
    }

    try {
        // Find the breaker to delete for change log
        const breakerToDelete = Object.values(formData).find(breaker => breaker.ID === sourceId);
        
        // Delete the specified drop pipe and refetch job details
        await dispatch(
            deleteData({ url: apiUrl, deleteId: { ID: sourceId } })
        ).unwrap();

        // Post to change log after successful deletion
        if (breakerToDelete) {
            // Create a descriptive string for the change log
            const breakerDescription = `(Quantity ${breakerToDelete.Quantity || "0"}) ${breakerToDelete.Amps || "0"} Amps, ${breakerToDelete.Voltage || "0"} Voltage`;
            
            await dispatch(postChangeLog({
                projectID: jobId,
                currentUser: currentUser,
                userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
                section: "Regulated Breakers",
                field: breakerDescription,
                prevValue: "",
                newValue: "DELETED",
                dateTime: new Date().toISOString()
            })).unwrap();
        }

        dispatch(clearSectionJobDetails(sectionName));

        const updatedDetails = await dispatch(
            fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })
        ).unwrap();

        setFormData((prevData) => {
            const updatedFormData = { ...prevData };
            if (updatedDetails[sectionName]) {
                updatedDetails[sectionName].forEach((source) => {
                    if (
                        source.Zone === activetab &&
                        source.ProjectID === jobDetails.ProjectID
                    ) {
                        updatedFormData[source.ID] = source;
                    }
                });
            }
            return updatedFormData;
        });
    } catch (error) {
        console.error("Failed to delete fill source:", error);
        setError(
            error.message || "An error occurred while deleting the fill source."
        );
    }
};

const fieldClasses = useFieldClass(formData, activetab); // Use useFieldClass hook

// Helper function to determine the highlight class for a field
const getFieldHighlightClass = (fieldName, sourceId) => {
    if (fieldClasses?.[sourceId]?.[fieldName]) {
        return fieldClasses[sourceId][fieldName]; // Use fieldClasses for dynamic highlighting
    }
    return ""; // Default class
};

if (loading) {
    return (
        <div className="d-flex justify-content-center align-items-center">
            <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );
}
return (
    <div className="electrical-breakers-responsive">
        {showNotification && (
            <div
                className={`save-notification ${
                    saveStatus.includes("Failed") ? "error" : ""
                }`}
            >
                {saveStatus}
            </div>
        )}
        {Object.keys(formData).map((sourceId) => {
            const source = formData[sourceId];

            return (
                <Row key={source.ID} className="mb-3 electrical-breaker-record">
                    {/* Quantity */}
                    <Col className="electrical-breaker-field">
                        <Form.Group as={Row} controlId={`quantity-${source.ID}`}>
                            <Form.Label column md={3}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={renderTooltip("Quantity")}
                                    delay={{ show: 300, hide: 200 }}
                                >
                                    <span>Quantity</span>
                                </OverlayTrigger>
                            </Form.Label>
                            <Col>
                                <Form.Control
                                    type="text"
                                    name="Quantity"
                                    value={source.Quantity || ""}
                                    onFocus={handleFocus}
                                    onBlur={(e) =>
                                        handleBlur(
                                            e,
                                            "Quantity",
                                            [],
                                            {
                                                ID: source.ID,
                                                url: apiUrl,
                                                field: "Quantity",
                                                tableKey,
                                            }
                                        )
                                    }
                                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                                    onChange={(e) => handleChange(e, source.ID)}
                                    placeholder="Enter Quantity"
                                    className={getFieldHighlightClass("Quantity", source.ID)} // Apply dynamic class
                                />
                            </Col>
                        </Form.Group>
                    </Col>

                    {/* Amps */}
                    <Col className="electrical-breaker-field">
                        <Form.Group as={Row} controlId={`amps-${source.ID}`}>
                            <Form.Label column md={3}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={renderTooltip("Amps")}
                                    delay={{ show: 300, hide: 200 }}
                                >
                                    <span>Amps</span>
                                </OverlayTrigger>
                            </Form.Label>
                            <Col>
                                <Form.Control
                                    type="text"
                                    name="Amps"
                                    value={source.Amps || ""}
                                    onFocus={handleFocus}
                                    onBlur={(e) =>
                                        handleBlur(
                                            e,
                                            "Amps",
                                            [],
                                            {
                                                ID: source.ID,
                                                url: apiUrl,
                                                field: "Amps",
                                                tableKey,
                                            }
                                        )
                                    }
                                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                                    onChange={(e) => handleChange(e, source.ID)}
                                    placeholder="Enter Amps"
                                    className={getFieldHighlightClass("Amps", source.ID)} // Apply dynamic class
                                />
                            </Col>
                        </Form.Group>
                    </Col>

                    {/* Voltage */}
                    <Col className="electrical-breaker-field">
                        <Form.Group as={Row} controlId={`voltage-${source.ID}`}>
                            <Form.Label column md={3}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={renderTooltip("Voltage")}
                                    delay={{ show: 300, hide: 200 }}
                                >
                                    <span>Voltage</span>
                                </OverlayTrigger>
                            </Form.Label>
                            <Col>
                                <Form.Control
                                    type="text"
                                    name="Voltage"
                                    value={source.Voltage || ""}
                                    onFocus={handleFocus}
                                    onBlur={(e) =>
                                        handleBlur(
                                            e,
                                            "Voltage",
                                            [],
                                            {
                                                ID: source.ID,
                                                url: apiUrl,
                                                field: "Voltage",
                                                tableKey,
                                            }
                                        )
                                    }
                                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                                    onChange={(e) => handleChange(e, source.ID)}
                                    placeholder="Enter Voltage"
                                    className={getFieldHighlightClass("Voltage", source.ID)} // Apply dynamic class
                                />
                            </Col>
                        </Form.Group>
                    </Col>

                    {/* Delete Button */}
                    <Col sm={1} className="electrical-breaker-actions">
                        <Button
                            variant="link"
                            onClick={() => handleDeleteFillSource(source.ID)}
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        >
                            <i className="bi bi-trash"></i>
                        </Button>
                    </Col>
                </Row>
            );
        })}
        <Button
            className="electrical-breaker-add-button"
            variant="primary"
            onClick={handleAddFillSource}
            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
        >
            Add Regulated Breaker
        </Button>
    </div>
);
};

export default RegulatedBreakers;
